// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React, { useState, useEffect } from 'react';

const FuelAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [topSavers, setTopSavers] = useState([]);
  const [submissions, setSubmissions] = useState(null);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    loadFuelAnalytics();
  }, []);

  const loadFuelAnalytics = async () => {
    setLoading(true);
    try {
      // Load overview analytics
      const analyticsResponse = await fetch(`${BACKEND_URL}/api/admin/fuel/analytics/overview`, {
        credentials: 'include'
      });
      const analyticsData = await analyticsResponse.json();
      setAnalytics(analyticsData);

      // Load top savers
      const saversResponse = await fetch(`${BACKEND_URL}/api/admin/fuel/analytics/top-savers?limit=5`, {
        credentials: 'include'
      });
      const saversData = await saversResponse.json();
      setTopSavers(saversData.top_savers || []);

      // Load submission analytics
      const submissionsResponse = await fetch(`${BACKEND_URL}/api/admin/fuel/analytics/community-submissions`, {
        credentials: 'include'
      });
      const submissionsData = await submissionsResponse.json();
      setSubmissions(submissionsData);
    } catch (error) {
      console.error('Error loading fuel analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="mt-8 bg-white rounded-xl p-8 shadow text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading fuel analytics...</p>
      </div>
    );
  }

  if (!analytics || !analytics.overview) {
    return (
      <div className="mt-8 bg-white rounded-xl p-8 shadow text-center">
        <p className="text-gray-600">No fuel analytics data available</p>
      </div>
    );
  }

  return (
    <div className="mt-8">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">⛽ Fuel Analytics</h2>
      
      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 shadow border-2 border-green-200">
          <p className="text-green-700 text-sm mb-2 font-semibold">Total Fuel Savings</p>
          <p className="text-4xl font-bold text-green-900">£{analytics.overview.total_savings.toFixed(2)}</p>
          <p className="text-sm text-green-600 mt-2">Across all users</p>
        </div>
        
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 shadow border-2 border-blue-200">
          <p className="text-blue-700 text-sm mb-2 font-semibold">Total Litres</p>
          <p className="text-4xl font-bold text-blue-900">{analytics.overview.total_litres.toFixed(0)}L</p>
          <p className="text-sm text-blue-600 mt-2">Fuel purchased</p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 shadow border-2 border-purple-200">
          <p className="text-purple-700 text-sm mb-2 font-semibold">Active Users</p>
          <p className="text-4xl font-bold text-purple-900">{analytics.overview.unique_users}</p>
          <p className="text-sm text-purple-600 mt-2">Using fuel features</p>
        </div>
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-xl p-6 shadow">
          <p className="text-gray-500 text-sm mb-2">Total Purchases</p>
          <p className="text-3xl font-bold text-gray-800">{analytics.overview.total_purchases}</p>
        </div>
        
        <div className="bg-white rounded-xl p-6 shadow">
          <p className="text-gray-500 text-sm mb-2">Avg Savings/User</p>
          <p className="text-3xl font-bold text-gray-800">£{analytics.overview.avg_savings_per_user.toFixed(2)}</p>
        </div>
        
        <div className="bg-white rounded-xl p-6 shadow">
          <p className="text-gray-500 text-sm mb-2">Avg Savings/Purchase</p>
          <p className="text-3xl font-bold text-gray-800">£{analytics.overview.avg_savings_per_purchase.toFixed(2)}</p>
        </div>
      </div>

      {/* Retailer Breakdown */}
      {analytics.retailer_breakdown && analytics.retailer_breakdown.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow mb-8">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Retailer Breakdown</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Retailer</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Purchases</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Total Savings</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Avg Savings</th>
                </tr>
              </thead>
              <tbody>
                {analytics.retailer_breakdown
                  .sort((a, b) => b.total_savings - a.total_savings)
                  .map((retailer, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium">{retailer.retailer}</td>
                      <td className="py-3 px-4 text-right">{retailer.purchase_count}</td>
                      <td className="py-3 px-4 text-right text-green-600 font-bold">
                        £{retailer.total_savings.toFixed(2)}
                      </td>
                      <td className="py-3 px-4 text-right">£{retailer.avg_savings.toFixed(2)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Savers Leaderboard */}
      {topSavers.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow mb-8">
          <h3 className="text-xl font-bold text-gray-800 mb-4">🏆 Top Fuel Savers</h3>
          <div className="space-y-3">
            {topSavers.map((saver, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-lg border-2 border-yellow-200"
              >
                <div className="flex items-center gap-4">
                  <div className="text-2xl font-bold text-yellow-600">#{index + 1}</div>
                  <div>
                    <p className="font-semibold text-gray-800">{saver.email}</p>
                    <p className="text-sm text-gray-600">
                      {saver.total_purchases} purchases • {saver.total_litres.toFixed(0)}L
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-600">£{saver.total_savings.toFixed(2)}</p>
                  <p className="text-xs text-gray-500">total saved</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Community Submissions */}
      {submissions && (
        <div className="bg-white rounded-xl p-6 shadow">
          <h3 className="text-xl font-bold text-gray-800 mb-4">📊 Community Price Submissions</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-800">{submissions.total_submissions}</p>
              <p className="text-sm text-gray-600">Total</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{submissions.verified}</p>
              <p className="text-sm text-gray-600">Verified</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <p className="text-2xl font-bold text-yellow-600">{submissions.pending}</p>
              <p className="text-sm text-gray-600">Pending</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <p className="text-2xl font-bold text-red-600">{submissions.rejected}</p>
              <p className="text-sm text-gray-600">Rejected</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{submissions.verification_rate}%</p>
              <p className="text-sm text-gray-600">Verified Rate</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FuelAnalytics;
